import { CSSResultGroup, html, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ChangeEvent, ValueFormElement } from 'elements/_shared/formElement.js'
import { style } from './radio-button-group.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { CSERadioButton } from 'elements/radio-button/radio-button.js'
import { classMap } from 'lit/directives/class-map.js'
import { groupJustifyOptionsType } from 'elements/group/group.js'


@customElement('cse-radio-button-group')
export class CSERadioButtonGroup extends ValueFormElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String })
  name = ''

  @property({ type: String })
  value: string = ''

  @property({ type: Boolean })
  inline = false

  @property({ type: String })
  justify: groupJustifyOptionsType[number] = 'start'

  @state()
  radioButtons: CSERadioButton[] = []

  private handleRadioChangeEvent = (value: string) => {
    this.value = value
    this.updateRadioSelection()
  }

  private handleRadioArrowKeyPress = (radioButtons: any, event: KeyboardEvent) => {
    if (['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'].includes(event.key)) {
      let targetIndex: number = -1
      const selectedIndex = radioButtons.findIndex((radio: CSERadioButton) => radio.checked)
  
      if (['ArrowUp', 'ArrowLeft'].includes(event.key)) { 
        targetIndex = selectedIndex > 0 ? selectedIndex-1 : radioButtons.length-1
      }
      
      if (['ArrowDown', 'ArrowRight'].includes(event.key)) {
        targetIndex = selectedIndex < radioButtons.length-1 ? selectedIndex+1 : 0
      }

      const nextRadioButton: CSERadioButton = radioButtons[targetIndex]
      nextRadioButton.shadowRoot?.querySelector('input')?.click()
      nextRadioButton.focus()
    }
  }

  private preventBrowserScroll = (event: KeyboardEvent) => {
    if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault()
    }
  }

  private updateRadioSelection = () => {
    this.radioButtons.forEach((radioButton) => {
      radioButton.checked = radioButton.value === this.value
    })
  }

  private handleSlotChange = () => {
    this.radioButtons = Array.from(this.querySelectorAll('cse-radio-button'))
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    if (changedProperties.has('value')) {
      this.updateRadioSelection()
    }
  }

  protected override firstUpdated(): void {
    this.radioButtons = Array.from(this.querySelectorAll('cse-radio-button'))
    this.updateRadioSelection()
  }

  override render() {
    return html`
      <div
        class="${classMap({ 
          'cse-radio-button-group' : true,
          'inline': this.inline
        })}"
        style="justify-content: ${this.justify};"
        @change="${(event: ChangeEvent) => this.handleRadioChangeEvent(event.value as string)}"
        @keydown="${(event: KeyboardEvent) => this.preventBrowserScroll(event)}"
        @keyup="${(event: KeyboardEvent) => this.handleRadioArrowKeyPress(this.radioButtons, event)}"
      >
        <slot @slotchange="${this.handleSlotChange}"></slot>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-radio-button-group': CSERadioButtonGroup
  }
}
